import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Svg,
  Circle,
  Link,
} from "@react-pdf/renderer";
import {
  capitalizeExceptCommonWords,
  convertDaysToDuration,
  formatAddressString,
  formatDateToString,
  formatFees,
  getApplicantFullName,
} from "services";
import { OfferCondition } from "types";

// React-PDF CSS compatible
const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 12,
    fontFamily: "Helvetica",
  },
  header: {
    textAlign: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 18,
    textAlign: "center",
    marginBottom: 10,
    fontWeight: "bold",
  },
  institution: {
    textAlign: "right",
    marginBottom: 20,
  },
  applicantDetails: {
    marginTop: 10,
    marginBottom: 20,
  },
  section: {
    lineHeight: 1.2,
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: "center",
    fontSize: 10,
    color: "#888",
  },
  message: {
    lineHeight: 0.9,
    flexDirection: "column",
    gap: 10,
    marginBottom: 15,
  },
  // constants
  h4: {
    fontWeight: 600,
  },
  textBold: {
    fontWeight: 600,
  },
  marginYSm: {
    marginVertical: 5,
  },
  marginYMd: {
    marginVertical: 10,
  },
  // logo
  logoContainer: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 10,
  },
  logo: {
    width: 25,
    height: 25,
    objectFit: "contain",
  },
  // table
  table: {
    width: "100%",
    fontSize: 12,
    lineHeight: 1.2,
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 2,
  },
  tableColLabel: {
    width: "30%",
    fontWeight: "bold",
  },
  tableColValue: {
    width: "70%",
    paddingLeft: 10,
  },
  tableHeaderRow: {
    flexDirection: "row",
    // paddingVertical: 3,
    paddingBottom: 10,
  },
  // bullet list
  numberedList: {
    marginLeft: 10,
  },
  listItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 2,
    marginLeft: 20,
  },
  bullet: {
    marginTop: 5,
    marginRight: 6,
  },
  listText: {
    flex: 1,
  },
});

interface ConditionalOfferLetterProps {
  data: any;
  conditions: string[];
  logoBase64: string | null;
}

// styling
const RowInfo = ({ label, value }: { label: string; value: any }) => {
  return (
    <View style={styles.tableRow}>
      <View style={styles.tableColLabel}>
        <Text>{label}</Text>
      </View>
      <View style={styles.tableColValue}>
        <Text>{value}</Text>
      </View>
    </View>
  );
};
const ListItem = ({ text }: { text: any }) => {
  return (
    <View style={styles.listItem}>
      <Svg width={4} height={4} style={styles.bullet}>
        <Circle cx="2" cy="2" r="2" fill="black" />
      </Svg>
      <Text style={styles.listText}>{text}</Text>
    </View>
  );
};

const renderCreditTransfer = (creditTransfer: any | null) => {
  if (!creditTransfer) {
    return null;
  }
  return (
    <View style={styles.message}>
      <View style={styles.numberedList}>
        <Text style={styles.h4}>Credit Transfer: {"\n"}</Text>
        <ListItem
          text={
            <>
              <Text style={styles.textBold}>Course: </Text>
              <Text>{`${creditTransfer.courseName} (${creditTransfer.courseCode})`}</Text>
            </>
          }
        />
        <ListItem
          text={
            <>
              <Text style={styles.textBold}>Institution: </Text>
              <Text>{creditTransfer.institutionName}</Text>
            </>
          }
        />
        <ListItem
          text={
            <>
              <Text style={styles.textBold}>Date Completed: </Text>
              <Text>{formatDateToString(creditTransfer.dateCompleted)}</Text>
            </>
          }
        />
      </View>
    </View>
  );
};

const ConditionalOfferLetter: React.FC<ConditionalOfferLetterProps> = ({
  data,
  conditions,
  logoBase64,
}) => {
  const basicDetails = data?.personalInformation?.basicDetails;
  const contactDetails = data?.personalInformation?.contactDetails;
  const educationDetails = data?.educationDetails;
  const courseDetails = data?.courseData;

  const date = new Date();
  const year = date.getFullYear();
  const lapseDate = new Date(date.setDate(date.getDate() + 14));

  const formattedInstitutionName = courseDetails.institutionName
    .replace(/\s*\([^)]*\)\s*/g, "")
    .trim();

  const formattedStudentAddress = () => {
    const a = contactDetails.homeAddress;
    if (!a) return "";

    return [
      a.street1,
      a.street2,
      [a.city, a.state, a.postalCode].filter(Boolean).join(", "),
      a.country,
    ]
      .filter(Boolean)
      .join("\n");
  };

  const estimatedAnnualFee =
    courseDetails.estimatedTotalCourseFee /
    (courseDetails.durationDay / 365.25);

  let conditionNumber = 1;
  const shouldShowCondition = (key: string) => {
    if (conditions.includes(OfferCondition.ALL)) {
      return true;
    }
    return conditions.includes(key);
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.institution}>
          {logoBase64 && (
            <View style={styles.logoContainer}>
              <Image
                style={styles.logo}
                src={{ uri: logoBase64, method: "GET", headers: {}, body: "" }}
              />
            </View>
          )}
          <Text>{formattedInstitutionName}</Text>
          <Text>{capitalizeExceptCommonWords(formatAddressString(courseDetails.address[0]))}</Text>
          <Text>{courseDetails.country}</Text>
        </View>

        <View style={styles.applicantDetails}>
          <Text style={styles.marginYMd}>{formatDateToString(new Date())}</Text>
          <Text>{getApplicantFullName(basicDetails)}</Text>
          <Text>{formattedStudentAddress()}</Text>
        </View>

        <View style={styles.section}>
          <Text>
            Dear {basicDetails.title} {basicDetails.lastName},
          </Text>
          <Text style={styles.textBold}>
            Subject: Conditional Offer of Admission
          </Text>
        </View>

        <View style={styles.message}>
          <Text style={styles.textBold}>
            Congratulations on your Conditional Offer!
          </Text>
          <Text>
            We are delighted to inform you that your application to study at the{" "}
            {formattedInstitutionName} has been conditionally approved.
          </Text>
          <Text>
            To secure your place, please read this letter carefully to ensure
            you understand all requirements and deadlines, and take the
            necessary steps to address the conditions outlined below.
          </Text>
          <Text>
            Here are some important details regarding your conditional offer:
          </Text>
        </View>

        {/* Conditional offer table */}
        <View style={styles.table}>
          <View style={styles.tableHeaderRow}>
            <Text style={styles.textBold}>Program Details</Text>
          </View>

          <RowInfo label="Program Name:" value={courseDetails.courseName} />
          {courseDetails.courseCode && (
            <RowInfo label="Program Code:" value={courseDetails.courseCode} />
          )}
          <RowInfo label="CRICOS Code:" value={courseDetails.courseId} />
          <RowInfo
            label="Campus:"
            value={capitalizeExceptCommonWords(formatAddressString(courseDetails.address[0]))}
          />
          <RowInfo
            label="Mode of Study:"
            value={`${courseDetails.programType}, ${courseDetails.modeOfStudy}`}
          />
          <RowInfo
            label="Expected Duration:"
            value={convertDaysToDuration(courseDetails.durationDay)}
          />
          {courseDetails.startDate && (
            <>
              <RowInfo
                label="Commencement Date:"
                value={courseDetails.startDate ?? "N/A"}
              />
              <RowInfo label="Compulsory Orientation:" value="N/A" />
            </>
          )}
          <RowInfo
            label={`Indicative Annual Tuition Fee (${year}):`}
            value={`${formatFees(estimatedAnnualFee)}`}
          />
          <RowInfo
            label="Estimated Total Tuition Fee for Full Program:"
            value={
              <View>
                <Text>
                  {`${formatFees(courseDetails.estimatedTotalCourseFee)}\n`}
                  {"\n"}
                </Text>
                <Text style={{ fontSize: 11, fontStyle: "italic" }}>
                  Please note: This tuition fee is indicative and represents
                  tuition fees for students commencing in {year} only. Tuition
                  fees are reviewed annually and may be subject to increase for
                  each calendar year of your study.
                  {"\n"}
                  Refer to the university website for more information regarding
                  current rates.
                </Text>
              </View>
            }
          />
          <RowInfo label="Lapse Date:" value={formatDateToString(lapseDate)} />
          {!educationDetails?.isApplyingForCreditTransfer ? (
            <RowInfo
              label="Credit:"
              value="No credit has been awarded for prior studies. If you would
              like to apply for a credit transfer, please request for a form."
            />
          ) : (
            <View style={{ marginTop: 10 }}>
              <Text style={styles.h4}>Credit:{"\n"}</Text>
              <Text style={{ marginBottom: 10 }}>
                The following credit towards your course has been approved on
                the bases of your previous studies: {"\n"}
              </Text>
              {renderCreditTransfer(educationDetails?.creditTransfer1)}
              {renderCreditTransfer(educationDetails?.creditTransfer2)}
              {renderCreditTransfer(educationDetails?.creditTransfer3)}
              {renderCreditTransfer(educationDetails?.creditTransfer4)}
              {renderCreditTransfer(educationDetails?.creditTransfer5)}
              {renderCreditTransfer(educationDetails?.creditTransfer6)}
              {renderCreditTransfer(educationDetails?.creditTransfer7)}
            </View>
          )}
        </View>

        {/* Offer Conditions */}
        <View style={styles.message}>
          <Text style={styles.textBold}>Your Offer Conditions</Text>
          <Text>
            This offer is conditional and is not a full offer of admission. To
            receive an unconditional offer and be eligible to receive a
            Confirmation of Enrolment (CoE) for your visa application, you must
            complete the conditions set out below by no later than{" "}
            {`${formatDateToString(lapseDate)}`} or this offer will lapse.
          </Text>
          <View>
            {shouldShowCondition(OfferCondition.ACADEMIC) && (
              <View style={styles.numberedList}>
                <Text style={styles.h4}>
                  {conditionNumber++}. Academic Verification:
                </Text>
                <ListItem
                  text="Provide certified digital copies (or high-quality scans) of your final academic transcript 
              and degree certificate (evidence of completion)"
                />
              </View>
            )}
            {shouldShowCondition(OfferCondition.ENGLISH_LANGUAGE) && (
              <View style={styles.numberedList}>
                <Text style={styles.h4}>
                  {conditionNumber++}. English Language Verification:
                </Text>
                <ListItem
                  text="Provide your official English language test result (e.g., IELTS, PTE Academic), with the Test 
            Report Form Number (TRFN) so we may verify your results directly with the test provider."
                />
                <ListItem
                  text="Your scores must meet the minimum requirement - such as IELTS Academic 6.5 overall with no band below 6.0, 
            PTE Academic 58 with at least 50 in each skill, or other approved evidence of proficiency."
                />
              </View>
            )}
            {shouldShowCondition(OfferCondition.GENUINE_STUDENT) && (
              <View style={styles.numberedList}>
                <Text style={styles.h4}>
                  {conditionNumber++}. Genuine Student (GS) Assessment:
                </Text>
                <ListItem text="Completed online Genuine Student (GS) Questionnaire." />
                <ListItem text="You will be required to upload supporting documentation (such as financial statements or a statement of purpose) to support your claims." />
              </View>
            )}
            {shouldShowCondition(OfferCondition.OSHC) && (
              <View style={styles.numberedList}>
                <Text style={styles.h4}>
                  {conditionNumber++}. Overseas Student Health Cover:
                </Text>
                <ListItem
                  text="You are required to maintain Overseas Student Health Cover (OSHC) for the duration of your studies. 
            OSHC needs to be arranged around the same time you accept your offer to study. The university can arrange OSHC on your behalf, 
            or you can purchase OSHC from one of the following current Australian Government-approved providers."
                />
              </View>
            )}
          </View>
          <Text>
            All tasks are to be completed within your account portal. Please
            monitor your email and notifications for the invitation to our
            Identity Verification service.
          </Text>
        </View>

        <View style={styles.message}>
          <Text style={styles.textBold}>Your Next Steps</Text>
          <View>
            <ListItem
              text={
                <>
                  <Text style={styles.textBold}>
                    Complete Your Conditions Online:
                  </Text>{" "}
                  The University must be satisfied that you meet the Australian
                  Department of Home Affairs Genuine Student (GS) and Genuine
                  Temporary Entrant (GTE) criteria and that you have the
                  financial capacity to cover both your tuition and living
                  expenses whilst studying in Australia. For further information
                  please visit the{" "}
                  <Link href="https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/student-500/genuine-temporary-entrant">
                    Department of Home Affairs’ website
                  </Link>
                  .
                </>
              }
            />
            <ListItem
              text={
                <>
                  <Text style={styles.textBold}>
                    Receive Your Unconditional Offer:
                  </Text>{" "}
                  Once our admissions team has verified all your submissions,
                  including a satisfactory Genuine Student assessment, we will
                  issue you an Unconditional Offer of Admission and an
                  Acceptance Agreement.
                </>
              }
            />
            <ListItem
              text={
                <>
                  <Text style={styles.textBold}>Accept Your Full Offer:</Text>{" "}
                  To accept your Unconditional Offer, you will need to digitally
                  review and sign the Acceptance Agreement, and make the
                  required deposit payment to secure your place.
                </>
              }
            />
          </View>
        </View>

        <View style={styles.message}>
          <Text style={styles.textBold}>
            Visa and Confirmation of Enrolment (CoE)
          </Text>
          <Text>
            Upon receipt of your digitally signed Acceptance Agreement and
            cleared deposit payment, {formattedInstitutionName} will issue your
            electronic Confirmation of Enrolment (CoE).
          </Text>
          <Text>
            The CoE is the official document you will need to apply for your
            Student visa (subclass 500). As part of your visa application, you
            will be required to answer a series of Genuine Student questions
            directly to the Department of Home Affairs. Our internal GS
            assessment is designed to prepare you for this, but the final
            decision on your visa rests with the Department.
          </Text>
          <Text>
            Please be aware that applying for your student visa is your
            responsibility, and we advise you to familiarise yourself with the
            process and requirements on their website:{" "}
            <Link href="https://immi.homeaffairs.gov.au/">
              Immigration and citizenship Website
            </Link>
            .
          </Text>
        </View>

        <View style={styles.message}>
          <Text style={styles.textBold}>
            Important Information for International Students
          </Text>
          <View>
            <ListItem
              text={
                <>
                  <Text style={styles.textBold}>ESOS Framework:</Text> As an
                  international student in Australia, your rights are protected
                  by the Education Services for Overseas Students (ESOS) Act
                  2000. You can learn more{" "}
                  <Link href="https://www.legislation.gov.au/C2004A00757/latest/text">
                    here
                  </Link>
                  .
                </>
              }
            />
            <ListItem
              text={
                <>
                  <Text style={styles.textBold}>Refund Policy:</Text> Our
                  policies on tuition fee refunds are outlined in the Acceptance
                  Agreement you will receive with your Full Offer.
                </>
              }
            />
            <ListItem
              text={
                <>
                  <Text style={styles.textBold}>Deferral:</Text> If you do not
                  meet your conditions by the deadline, you may be able to defer
                  your offer to the next available intake. Please contact us to
                  discuss your options.
                </>
              }
            />
          </View>
        </View>

        <View style={styles.message}>
          <Text>
            We congratulate you once again on this conditional offer and are
            excited about the prospect of you joining us at{" "}
            {formattedInstitutionName}. If you have any questions regarding your
            offer or these conditions, please do not hesitate to contact the
            International Admissions Office.
          </Text>
        </View>

        <View style={styles.section}>
          <Text>Sincerely,</Text>
          <Text>Admissions Team</Text>
        </View>

        <Text style={styles.footer}>
          © 2025 EDUBAY Institute | Confidential
        </Text>
      </Page>
    </Document>
  );
};

export default ConditionalOfferLetter;
